'use client'

import { type Api, lookup, type Spotted } from '@cruz-com/lookup'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import _ from 'lodash'
import { useParams, usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { spottedApi } from '@/api'
import { Box, Divider, H1, LinkButton, PageMeta, Pagination, RadioButtons, SpottedCard } from '@/components'
import { encodeQuery } from '@/lib'
import { pages } from '@/settings'
import { useGlobalStore } from '@/store/provider'

type Query = {
  user_id?: string
  page_size?: number
  page_number?: number
  sort_id?: number
}

type Props = {
  myQuery: Query
  initialData?: Api.Result<Spotted.Summary[]> | undefined
}

export function UserSpotted({ myQuery, initialData }: Props) {
  const { name, id } = useParams()
  const pathname = usePathname()
  const params = useSearchParams()
  const { spottedFilter, setSpottedFilter } = useGlobalStore((state) => state)

  // init query
  const [query, setQuery] = useState<Query>(() => ({
    ...spottedFilter,
    ...myQuery,
    user_id: id as string,
    ...Object.fromEntries(params.entries()),
  }))

  // Keep query in sync if the user navigates using back/forward browser buttons
  const isPushingState = useRef(false)
  const isMounted = useRef(false)
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return
    }
    if (isPushingState.current) {
      isPushingState.current = false
      return
    }
    const pQuery = Object.fromEntries(params.entries())
    if (_.isEmpty(pQuery)) {
      setQuery({ ...spottedFilter, page_number: 1, user_id: id as string })
      return
    }
    const urlQuery = { ...query, ...pQuery, user_id: id as string }
    if (!_.isEqual(query, urlQuery)) {
      setQuery(urlQuery)
    }
  }, [params])

  const { data, isFetching } = useQuery({
    queryKey: ['UserSpotted', query],
    queryFn: () => spottedApi.search(query),
    staleTime: 5000,
    initialData: _.isEqual(query, { ...spottedFilter, ...myQuery, user_id: id as string }) ? initialData : undefined,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  })

  function filterChanged(name: string, value: string | number) {
    updateQuery({ ...query, page_number: 1, [name]: value })
  }

  function pageNumberChanged(pageNumber: number) {
    updateQuery({ ...query, page_number: pageNumber })
  }

  function updateQuery(q: Query) {
    isPushingState.current = true
    setQuery(q)
    setSpottedFilter(q)
    window.history.pushState(null, '', `${pathname}?${encodeQuery(q)}`)
  }

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [query])

  const totalRecordsMatched = data?.meta?.total_records_matched || 0
  const totalPages = data?.meta?.total_pages || 0

  if (totalRecordsMatched === 0) {
    return <Box className='font-semibold!'>No Spotted from {name?.toString().replace('-', ' ')}</Box>
  }

  return (
    <>
      <div className='flex items-baseline justify-between'>
        <H1>{pages.userSpotted.name}</H1>
        <div>
          <LinkButton
            label={pages.postSpotted.name}
            href={pages.postSpotted.href}
            variant='secondary'
            icon='new'
            className='rounded-full px-4'
          />
        </div>
      </div>
      <Divider className='-mt-2 mb-3' />
      <section aria-label='Page size and sort options' className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
        <PageMeta
          meta={data?.meta || {}}
          pageSize={_.toNumber(query?.page_size)}
          onPageSizeChanged={(val) => {
            filterChanged('page_size', val)
          }}
        />
        <div className='flex items-center gap-3'>
          <label htmlFor='sort_id' className='text-sm font-light' aria-hidden='true'>
            Sort:
          </label>
          <RadioButtons
            name='sort_id'
            option={lookup.spotted.sortOption}
            value={_.toNumber(query.sort_id)}
            onChange={(val) => {
              filterChanged('sort_id', _.toNumber(val))
            }}
          />
        </div>
      </section>
      <ul
        aria-label='Spotted list'
        className={twMerge('mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-6 xl:grid-cols-3', isFetching ? 'opacity-0' : '')}
      >
        {_.map(data?.data, (item) => (
          <li key={item._id as string} className='li-card'>
            <SpottedCard d={item} showUser={false} showPhotoCount={false} />
          </li>
        ))}
      </ul>
      {totalPages > 1 && <Pagination meta={data?.meta || {}} onPageNumberChanged={(val) => pageNumberChanged(val)} />}
    </>
  )
}
